/**
 * Settings API hooks
 * Covers: Tags management, User Types, Shareable Link
 *
 * Endpoint reference (ported from mobile urls.dart):
 *   GET/POST   /custom_account/tags/
 *   PATCH/DEL  /custom_account/tags/:id/
 *   PATCH      /custom_account/update-profile/  → update user tags (array of IDs)
 *   GET        /custom_account/user-types/
 *   PATCH      /profiles/edit-profile/          → update user_types
 *   GET/PATCH  /profiles/settings/shareable-link/
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

// ── Tag ───────────────────────────────────────────────────────────────────────

export interface Tag {
  id: number;
  name: string;
  is_global: boolean;
  creator: number | null;
}

/** Fetch all available tags from the platform */
export const useGetAllTags = () =>
  useQuery<Tag[]>({
    queryKey: ['tags'],
    queryFn: async () => {
      const { data } = await apiClient.get('/custom_account/tags/');
      return Array.isArray(data) ? data : (data?.results ?? data?.data ?? []);
    },
    staleTime: 5 * 60 * 1000,
  });

/** Create a new custom tag */
export const useCreateTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await apiClient.post('/custom_account/tags/', { name });
      const payload = (data?.data ?? data?.result ?? data) as Partial<Tag> | undefined;
      if (payload && typeof payload.id === 'number') {
        return payload as Tag;
      }
      throw new Error('Create tag response is missing a valid id.');
    },
    onSuccess: (created) => {
      toast.success('Tag created');
      qc.setQueryData<Tag[]>(['tags'], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        if (list.some((tag) => tag.id === created.id)) return list;
        return [created, ...list];
      });
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to create tag')),
  });
};

/** Update a tag by ID – only the creator can edit a non-global tag */
export const useUpdateTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const { data } = await apiClient.patch(`/custom_account/tags/${id}/`, { name });
      const payload = (data?.data ?? data?.result ?? data) as Partial<Tag> | undefined;
      if (payload && typeof payload.id === 'number') {
        return payload as Tag;
      }
      throw new Error('Update tag response is missing a valid id.');
    },
    onSuccess: () => {
      toast.success('Tag updated');
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to update tag')),
  });
};

/** Delete a tag by ID – only the creator can delete a non-global tag */
export const useDeleteTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/custom_account/tags/${id}/`);
    },
    onSuccess: () => {
      toast.success('Tag deleted');
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to delete tag')),
  });
};

/**
 * Update the authenticated user's selected tags.
 * Sends an array of tag IDs to PATCH /custom_account/update-profile/
 */
export const useUpdateUserTags = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagIds: number[]) => {
      const { data } = await apiClient.patch('/custom_account/update-profile/', { tags: tagIds });
      return data;
    },
    onSuccess: () => {
      toast.success('Tags updated successfully');
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['public-profile'] });
      qc.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to update tags')),
  });
};

// ── User Types ────────────────────────────────────────────────────────────────

export interface UserType {
  id: number;
  name: string;
  description?: string;
}

/**
 * Fetch all available user types.
 * Filters out 'fan' and 'admin' to match mobile behaviour.
 */
export const useGetUserTypes = () =>
  useQuery<UserType[]>({
    queryKey: ['user-types'],
    queryFn: async () => {
      const { data } = await apiClient.get('/custom_account/user-types/');
      const list: UserType[] = Array.isArray(data) ? data : (data?.results ?? data?.data ?? []);
      return list.filter((t) => !['fan', 'admin'].includes(t.name.toLowerCase()));
    },
    staleTime: 10 * 60 * 1000,
  });

/**
 * Update the authenticated user's role types.
 * Mobile sends multipart/form-data with user_types as a JSON string to /profiles/edit-profile/
 */
export const useUpdateUserTypes = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (selectedNames: string[]) => {
      const userTypesPayload = selectedNames.map((name) => ({ name, tags: [] }));
      const formData = new FormData();
      formData.append('user_types', JSON.stringify(userTypesPayload));
      const { data } = await apiClient.patch('/profiles/edit-profile/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      toast.success('User type updated successfully');
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['public-profile'] });
      qc.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to update user type')),
  });
};

// ── Shareable Link ─────────────────────────────────────────────────────────────

export interface ShareableLinkData {
  shareable_link_active: boolean;
  /** Map of platform API keys → visible boolean */
  visible_social_links: Record<string, boolean>;
}

/** Fetch the current shareable link settings */
export const useGetShareableLink = () =>
  useQuery<ShareableLinkData>({
    queryKey: ['shareable-link'],
    queryFn: async () => {
      const { data } = await apiClient.get('/profiles/settings/shareable-link/');
      return data?.data ?? data ?? { shareable_link_active: false, visible_social_links: {} };
    },
    staleTime: 2 * 60 * 1000,
  });

/** Update shareable link settings.
 * Sends multipart/form-data and only includes the fields present in payload
 * (mirrors the mobile patchFormData pattern in shareable_link.dart).
 */
export const useUpdateShareableLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<ShareableLinkData>) => {
      const formData = new FormData();
      if ('shareable_link_active' in payload) {
        formData.append('shareable_link_active', String(payload.shareable_link_active));
      }
      if ('visible_social_links' in payload && payload.visible_social_links !== undefined) {
        formData.append('visible_social_links', JSON.stringify(payload.visible_social_links));
      }
      const { data } = await apiClient.patch('/profiles/settings/shareable-link/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data?.data ?? data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shareable-link'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to update shareable link')),
  });
};

// ── Genres ────────────────────────────────────────────────────────────────────

export interface Genre {
  id: number;
  name: string;
  slug?: string;
  is_global: boolean;
  creator: number | null;
}

/** Fetch all available genres (GET /custom_account/genres/) */
export const useGetAllGenres = () =>
  useQuery<Genre[]>({
    queryKey: ['genres'],
    queryFn: async () => {
      const { data } = await apiClient.get('/custom_account/genres/');
      const list: unknown = Array.isArray(data) ? data : (data?.results ?? data?.data ?? []);
      return list as Genre[];
    },
    staleTime: 5 * 60 * 1000,
  });

/** Create a new custom genre (POST /custom_account/genres/) */
export const useCreateGenre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await apiClient.post('/custom_account/genres/', { name });
      const payload = (data?.data ?? data?.result ?? data) as Partial<Genre> | undefined;
      if (payload && typeof payload.id === 'number') {
        return payload as Genre;
      }
      throw new Error('Create genre response is missing a valid id.');
    },
    onSuccess: (created) => {
      toast.success('Genre created');
      qc.setQueryData<Genre[]>(['genres'], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        if (list.some((genre) => genre.id === created.id)) return list;
        return [created, ...list];
      });
      qc.invalidateQueries({ queryKey: ['genres'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to create genre')),
  });
};

/** Update a genre by ID (PATCH /custom_account/genres/:id/) – creator only */
export const useUpdateGenre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const { data } = await apiClient.patch(`/custom_account/genres/${id}/`, { name });
      return data as Genre;
    },
    onSuccess: () => {
      toast.success('Genre updated');
      qc.invalidateQueries({ queryKey: ['genres'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to update genre')),
  });
};

/** Delete a genre by ID (DELETE /custom_account/genres/:id/) – creator only */
export const useDeleteGenre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/custom_account/genres/${id}/`);
    },
    onSuccess: () => {
      toast.success('Genre deleted');
      qc.invalidateQueries({ queryKey: ['genres'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to delete genre')),
  });
};

/**
 * Save the user's selected genres.
 * Mirrors mobile: PATCH multipart/form-data to /custom_account/update-profile/{userId}/
 * with `genres` as a JSON-encoded array of genre names.
 */
export const useUpdateUserGenres = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      genreNames,
    }: {
      userId: string | number;
      genreNames: string[];
    }) => {
      const formData = new FormData();
      formData.append('genres', JSON.stringify(genreNames));
      const { data } = await apiClient.patch(
        `/custom_account/update-profile/${userId}/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Genres updated successfully');
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['public-profile'] });
    },
    onError: (err: any) => toast.error(extractApiError(err, 'Failed to update genres')),
  });
};
